// Matematica del lienzo del diagrama de flujo. Sin React, sin JSX y sin estado:
// todo son funciones puras, asi que es la unica parte del editor que se puede
// razonar y probar aislada. Si FlowCanvas crece, lo que sale es codigo hacia
// aqui, no mas JSX.

// ── Geometria de los nodos ───────────────────────────────────────────────────
// Un PASO es una tarea; los demas tipos son nodos de control del flujo.
export const NODE_GEO = {
  // 84 y no 64. Con 64 el titulo, los participantes y la fecha se peleaban por
  // la misma franja: el titulo se recortaba a 22 caracteres pero con tres
  // avatares solo le quedaba sitio para unos 16, asi que se metia por debajo de
  // ellos y quedaba ilegible justo el dato mas importante del nodo. Ahora cada
  // cosa tiene su banda —titulo, tipo y fecha, participantes— y el titulo
  // dispone del ancho completo.
  //
  // Sigue por debajo del rowGap del auto-layout (104), que es lo que impide que
  // un nodo se solape con el de arriba o el de abajo de su misma columna.
  tarea:       { w: 196, h: 84,  rx: 10 },
  // Dos CIRCULOS con un icono dentro, y nada mas. Un inicio y un fin dicen una
  // sola cosa —"por aqui empieza", "por aqui acaba"— y no necesitan ni nombre ni
  // caja: el triangulo de play y la bandera de meta ya lo dicen, y son los
  // mismos iconos con los que se crean desde la paleta.
  //
  // Antes eran pildoras de 40x96 con el nombre apilado letra a letra. Se leia,
  // pero costaba tres veces mas alto que un hito para decir menos, y el nombre
  // era casi siempre la palabra "Inicio" o "Fin", o sea el rotulo del icono.
  //
  // 40 tambien de alto: cabe de sobra en el rowGap del auto-layout (104), es
  // multiplo de la rejilla, y con w = h la pildora sale circulo exacto sin
  // tocar nada mas (el radio se calcula con el lado corto).
  inicio:      { w: 40, h: 40, rx: 20 },
  fin:         { w: 40, h: 40, rx: 20 },
  // Rombo achatado, no cuadrado: la banda central es la unica que aguanta
  // texto, y 124 de alto se comian un carril entero del diagrama para decir
  // una sola pregunta.
  condicional: { w: 132, h: 88,  rx: 0  },
  hito:        { w: 34,  h: 34,  rx: 0  },   // diamante pequeño
  // El switch crece con sus salidas: su alto real lo calcula geoDe.
  switch:      { w: 196, h: 88,  rx: 10 },
};

// Una etiqueta que solo repite el nombre de su rama NO es una etiqueta: es el
// mismo dato que ya dice el color del puerto y de la linea, escrito otra vez
// encima de la flecha. Se ignora al pintar y al editar.
//
// Hace falta porque la etiqueta "Si"/"No" esta GUARDADA en la base de datos:
// se escribia al crear la arista, asi que quitar el rotulo derivado no basta
// —las aristas que ya existen la traen puesta—. Y como el dato de que rama es
// vive en `from_handle`, que tambien se guarda, la etiqueta era una copia.
//
// Se compara sin acentos ni mayusculas: en la base hay "Si" de cuando lo
// escribia el lienzo y puede haber "Sí" escrito por una persona.
const NOMBRES_RAMA = { si: ["si", "sí"], no: ["no"] };
export function etiquetaPropia(e) {
  const t = String(e?.etiqueta || "").trim().toLowerCase();
  if (!t) return null;
  const suyos = NOMBRES_RAMA[e?.from_handle];
  return suyos && suyos.includes(t) ? null : e.etiqueta;
}

// Salidas de un switch: [{clave,nombre}].
//
// Las claves son estables y NO se reutilizan. Si fueran la posicion del array,
// borrar o reordenar una salida remapearia en silencio las aristas existentes a
// la salida equivocada, que es la peor clase de fallo de datos.
//
// La entrada de clave "resto" es la salida por defecto —la que recoge lo que no
// encaja en ninguna otra— y es opcional.
export const SALIDAS_POR_DEFECTO = [
  { clave: "s1", nombre: "Opcion 1" },
  { clave: "s2", nombre: "Opcion 2" },
];

export function salidasDe(n) {
  if (n?.kind !== "switch") return [];
  const s = n.salidas;
  if (!Array.isArray(s) || !s.length) return SALIDAS_POR_DEFECTO;
  return s.filter(x => x && x.clave).map(x => ({
    clave: String(x.clave),
    nombre: String(x.nombre ?? x.clave),
  }));
}

// Clave nueva que no choque con ninguna usada ni con las ya borradas: se toma el
// mayor sN visto y se suma uno.
export function claveNueva(salidas = []) {
  let max = 0;
  for (const s of salidas) {
    const m = /^s(\d+)$/.exec(String(s?.clave || ""));
    if (m) max = Math.max(max, Number(m[1]));
  }
  return "s" + (max + 1);
}

const ALTO_SALIDA = 22;

export function geoDe(n) {
  const base = NODE_GEO[n?.kind] || NODE_GEO.tarea;
  if (n?.kind !== "switch") return base;
  // Alto suficiente para el titulo mas una linea por salida. Si el nodo no
  // crece, las salidas se apinan y los puertos se solapan.
  const n_ = salidasDe(n).length;
  return { ...base, h: Math.max(base.h, 34 + n_ * ALTO_SALIDA + 10) };
}

// Caja real de un nodo: el ancho y alto guardados manda sobre el de su tipo,
// que es lo que permitira redimensionar un agrupador mas adelante.
export function nodeBox(n) {
  const g = geoDe(n);
  return {
    x: Number(n.pos_x) || 0,
    y: Number(n.pos_y) || 0,
    w: Number(n.ancho) || g.w,
    h: Number(n.alto)  || g.h,
  };
}

export const K_MIN = 0.3, K_MAX = 2.5;
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const GRID = 8;
export const snap = v => Math.round(v / GRID) * GRID;

// ── Pantalla <-> lienzo ──────────────────────────────────────────────────────
// Se usa viewBox y no un transform anidado: asi la conversion es una division y
// no hay que invertir una matriz acumulada (getScreenCTM().inverse() devuelve
// nulos cuando el contenedor pasa por display:none, y aqui el panel deslizante
// reflowea el contenedor).
export const viewBoxOf = (view, rect) =>
  `${view.x} ${view.y} ${Math.max(1, rect.width / view.k)} ${Math.max(1, rect.height / view.k)}`;

// El viewBox mide rect/k unidades de lienzo, asi que 1 unidad = k px de pantalla.
export const screenToCanvas = (cx, cy, rect, view) => ({
  x: view.x + (cx - rect.left) / view.k,
  y: view.y + (cy - rect.top)  / view.k,
});

export const canvasToScreen = (p, rect, view) => ({
  left: rect.left + (p.x - view.x) * view.k,
  top:  rect.top  + (p.y - view.y) * view.k,
});

// Zoom hacia el cursor: se conserva el punto de lienzo que hay bajo el raton.
//   view.x + dx/k = view.x2 + dx/k2   =>   view.x2 = view.x + dx*(1/k - 1/k2)
export function zoomAt(view, cx, cy, factor, rect) {
  const k2 = clamp(view.k * factor, K_MIN, K_MAX);
  if (k2 === view.k) return view;
  const dx = cx - rect.left, dy = cy - rect.top;
  return {
    k: k2,
    x: view.x + dx * (1 / view.k - 1 / k2),
    y: view.y + dy * (1 / view.k - 1 / k2),
  };
}

// Pan: arrastrar el fondo d pixeles desplaza la vista d/k unidades, en sentido
// contrario. Se parte SIEMPRE de la vista del pointerdown (view0) en vez de
// acumular, porque acumular deriva medio pixel por evento.
export const panDesde = (view0, k, dxPx, dyPx) => ({
  k, x: view0.x - dxPx / k, y: view0.y - dyPx / k,
});

// ── Caja envolvente y encaje ─────────────────────────────────────────────────
export function bbox(nodos) {
  const lista = Array.isArray(nodos) ? nodos : Object.values(nodos || {});
  if (!lista.length) return { x: 0, y: 0, w: 1, h: 1 };
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const n of lista) {
    const b = nodeBox(n);
    x1 = Math.min(x1, b.x);      y1 = Math.min(y1, b.y);
    x2 = Math.max(x2, b.x + b.w); y2 = Math.max(y2, b.y + b.h);
  }
  return { x: x1, y: y1, w: Math.max(1, x2 - x1), h: Math.max(1, y2 - y1) };
}

// Suelo de zoom para el ENCAJE, mas bajo que K_MIN.
//
// K_MIN (0,3) es el limite del zoom a mano: mas lejos no se lee nada y no tiene
// sentido dejar que la rueda llegue ahi. Pero "ver todo" es una peticion
// explicita de ver TODO, asi que tiene que poder alejarse lo que haga falta o
// deja nodos fuera justo cuando se le esta pidiendo lo contrario.
//
// 0,05 no es un numero redondo por gusto: es lo que hace falta para que quepa un
// diagrama de unos 16.000 px de ancho en una pantalla de 800, que es mas de lo
// que da de si una colocacion automatica de sesenta pasos. A esa escala un paso
// mide 10 px —se ve que hay algo, no se lee— y eso solo pasa en un diagrama
// disparatado; en cuanto se reorganiza, vuelve a una escala normal.
export const K_FIT_MIN = 0.05;

// "Ver todo". El tope superior es 1.2 y no K_MAX a proposito: con dos nodos,
// encajar a 2,5x da un diagrama grotesco.
// `tapadoIzq` son los pixeles de la izquierda del lienzo que hay algo encima:
// hoy, el panel del paso seleccionado. El SVG sigue midiendo lo mismo —el panel
// flota sobre el, no lo encoge—, asi que sin este dato encajar la vista centraba
// el diagrama en el lienzo ENTERO y media pantalla de nodos acababa debajo del
// panel. Se encaja en el hueco que de verdad se ve.
export function fitView(nodos, rect, pad = 48, tapadoIzq = 0) {
  const b = bbox(nodos);
  const libre = Math.max(120, rect.width - tapadoIzq);
  const k = clamp(
    Math.min(libre / (b.w + pad * 2), rect.height / (b.h + pad * 2)),
    K_FIT_MIN, 1.2
  );
  return {
    k,
    // El centro del diagrama tiene que caer en el centro del HUECO, que esta
    // desplazado tapadoIzq a la derecha respecto al centro del lienzo.
    x: b.x + b.w / 2 - (tapadoIzq + libre / 2) / k,
    y: b.y + b.h / 2 - rect.height / (2 * k),
  };
}

// ¿Esta este punto dentro de lo que se ve ahora mismo?
//
// Lo usa el atajo de insertar: si el puntero esta fuera del lienzo (sobre el
// panel, sobre la barra, o fuera de la ventana), el nodo no debe nacer en la
// ultima posicion conocida —que puede estar en cualquier parte— sino en el
// centro de la vista.
// ── Marquesina de seleccion ─────────────────────────────────────────────────

// Rectangulo normalizado a partir de los dos puntos de un arrastre. Se
// normaliza porque se puede arrastrar en las cuatro direcciones y un ancho
// negativo no lo pinta SVG.
export const rectDeDosPuntos = (a, b) => ({
  x: Math.min(a.x, b.x), y: Math.min(a.y, b.y),
  w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y),
});

// Que nodos toca un rectangulo.
//
// Basta que se SOLAPEN: no hace falta que el nodo quede entero dentro. Exigir
// que envuelva un paso de 196x64 completo obliga a barridos enormes y falla
// justo cuando la marquesina roza el borde de un nodo que se ve claramente
// dentro, que es la queja tipica de estos editores.
export function nodosEnRect(nodos, r) {
  const dentro = [];
  for (const id of Object.keys(nodos || {})) {
    const b = nodeBox(nodos[id]);
    if (b.x < r.x + r.w && b.x + b.w > r.x && b.y < r.y + r.h && b.y + b.h > r.y)
      dentro.push(String(id));
  }
  return dentro;
}

export function dentroDeLaVista(p, rect, view, margen = 0) {
  if (!p) return false;
  const w = rect.width / view.k, h = rect.height / view.k;
  return p.x >= view.x + margen && p.x <= view.x + w - margen
      && p.y >= view.y + margen && p.y <= view.y + h - margen;
}

// ── Impactos ─────────────────────────────────────────────────────────────────
// El ultimo del orden de pintado gana: en SVG el orden z es el del documento, y
// lo que esta encima es lo que se ve.
export function hitNode(nodos, p, orden) {
  const ids = orden && orden.length ? orden : Object.keys(nodos || {});
  for (let i = ids.length - 1; i >= 0; i--) {
    const n = nodos[ids[i]];
    if (!n) continue;
    const b = nodeBox(n);
    if (n.kind === "condicional") {
      // Rombo: |dx|/(w/2) + |dy|/(h/2) <= 1
      const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
      if (Math.abs(p.x - cx) / (b.w / 2) + Math.abs(p.y - cy) / (b.h / 2) <= 1) return n;
      continue;
    }
    if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) return n;
  }
  return null;
}

// ── Puertos ──────────────────────────────────────────────────────────────────
// `dir` apunta SIEMPRE hacia fuera del nodo. Es lo que hace que la arista salga
// perpendicular en vez de en diagonal, sin casos especiales.
export const PORT_DIR = { in: [-1, 0], out: [1, 0], si: [1, 0], no: [0, 1] };

// Que puertos tiene un nodo. Un 'inicio' no recibe y un 'fin' no emite: ofrecer
// el puerto invitaria a dibujar un flujo imposible.
//
// Recibe el NODO y no solo su kind, porque las salidas de un switch dependen de
// sus datos y no de su tipo.
export function puertosDe(n) {
  const kind = typeof n === "string" ? n : n?.kind;
  if (kind === "inicio")      return { in: false, salidas: ["out"] };
  if (kind === "fin")         return { in: true,  salidas: [] };
  if (kind === "condicional") return { in: true,  salidas: ["si", "no"] };
  if (kind === "switch")
    return { in: true, salidas: salidasDe(typeof n === "string" ? null : n).map(s => s.clave) };
  return { in: true, salidas: ["out"] };
}

export function portPoint(n, port) {
  const b = nodeBox(n);
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
  let p;
  if (n.kind === "switch") {
    if (port === "in") return { x: b.x, y: cy, dir: PORT_DIR.in };
    // Una salida por linea, alineada con su nombre en el dibujo del nodo.
    const ss = salidasDe(n);
    const i = Math.max(0, ss.findIndex(s => s.clave === port));
    return {
      x: b.x + b.w,
      y: b.y + 34 + i * ALTO_SALIDA + ALTO_SALIDA / 2 - 4,
      dir: PORT_DIR.out,
    };
  }
  if (n.kind === "condicional") {
    if (port === "in")      p = { x: b.x,       y: cy };
    else if (port === "no") p = { x: cx,        y: b.y + b.h };
    else                    p = { x: b.x + b.w, y: cy };   // 'si' y 'out'
  } else {
    p = port === "in" ? { x: b.x, y: cy } : { x: b.x + b.w, y: cy };
  }
  return { ...p, dir: PORT_DIR[port] || PORT_DIR.out };
}

// ── Aristas ──────────────────────────────────────────────────────────────────
// Bezier cubica con los puntos de control sobre la normal del puerto.
export function edgePath(a, b) {
  const dist = Math.hypot(b.x - a.x, b.y - a.y);
  // Retroceso: el destino queda DETRAS del puerto de salida, que es lo que pasa
  // en un bucle de retrabajo. Con la panza normal la curva se metia dentro del
  // propio nodo, asi que se le da mucho mas vuelo.
  const back = (b.x - a.x) * a.dir[0] + (b.y - a.y) * a.dir[1] < 0;
  const d = clamp(dist * (back ? 0.8 : 0.42), 46, back ? 240 : 150);
  const c1 = { x: a.x + a.dir[0] * d, y: a.y + a.dir[1] * d };
  // El dir de b tambien apunta hacia fuera, asi que su control cae ANTES del
  // destino y la flecha entra perpendicular.
  const c2 = { x: b.x + b.dir[0] * d, y: b.y + b.dir[1] * d };
  return {
    d: `M${a.x},${a.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${b.x},${b.y}`,
    c1, c2, back,
  };
}

// Punto medio de la cubica en t=0.5, por formula cerrada:
//   P(0.5) = (A + 3·C1 + 3·C2 + B) / 8
// Muestrear la curva para esto seria trabajo de mas y menos exacto.
export const midPoint = (a, c1, c2, b) => ({
  x: (a.x + 3 * c1.x + 3 * c2.x + b.x) / 8,
  y: (a.y + 3 * c1.y + 3 * c2.y + b.y) / 8,
});

// Trazado completo de una arista a partir de los nodos y sus puertos.
export function edgeGeom(edge, nodos) {
  const from = nodos[String(edge.from_task_id)];
  const to   = nodos[String(edge.to_task_id)];
  if (!from || !to) return null;
  const a = portPoint(from, edge.from_handle || "out");
  const b = portPoint(to,   edge.to_handle   || "in");
  const g = edgePath(a, b);
  return { ...g, a, b, mid: midPoint(a, g.c1, g.c2, b) };
}

// ── Intercalar un nodo en una arista ─────────────────────────────────────────
// Un nodo se puede meter EN MEDIO de una flecha si tiene por donde entrar y por
// donde salir. Un inicio no recibe y un fin no emite, asi que ninguno de los dos
// puede partir un camino: quedaria un tramo muerto.
export function puedeIntercalarse(n) {
  const pu = puertosDe(n);
  return !!pu.in && pu.salidas.length > 0;
}

// Un nodo SUELTO: el que no toca ninguna flecha, ni de entrada ni de salida.
// Solo estos se intercalan. Uno que ya esta conectado, al soltarlo sobre una
// linea, se quedaria con las dos flechas nuevas ENCIMA de las que ya tenia, y
// eso es ensuciar el diagrama sin decirlo; ademas el gesto natural con un paso
// ya colocado es moverlo, no recablearlo.
export function nodoSuelto(id, edges) {
  const k = String(id);
  return !Object.values(edges || {}).some(e =>
    String(e.from_task_id) === k || String(e.to_task_id) === k);
}

// Un punto de la cubica, por el polinomio de Bernstein.
function puntoCubica(a, c1, c2, b, t) {
  const u = 1 - t, u2 = u * u, t2 = t * t;
  return {
    x: u2 * u * a.x + 3 * u2 * t * c1.x + 3 * u * t2 * c2.x + t2 * t * b.x,
    y: u2 * u * a.y + 3 * u2 * t * c1.y + 3 * u * t2 * c2.y + t2 * t * b.y,
  };
}

// Distancia de un punto al segmento AB. La proyeccion se acota a [0,1] para que
// un punto mas alla de un extremo mida hasta el extremo y no hasta la recta
// infinita, que es lo que haria "picar" aristas que ni se rozan.
function distAlSegmento(p, a, b) {
  const vx = b.x - a.x, vy = b.y - a.y;
  const largo2 = vx * vx + vy * vy;
  if (largo2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / largo2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
}

// Distancia de un punto a una arista. La cubica se aproxima por una poligonal:
// con MUESTRAS tramos el error es de decimas de pixel a estos tamaños, y evita
// tener que resolver la quintica de la distancia exacta o depender del DOM
// (getPointAtLength), que no existe fuera del navegador y no se podria probar.
const MUESTRAS = 24;
export function distanciaAArista(g, p) {
  let min = Infinity, previo = g.a;
  for (let i = 1; i <= MUESTRAS; i++) {
    const actual = i === MUESTRAS ? g.b : puntoCubica(g.a, g.c1, g.c2, g.b, i / MUESTRAS);
    const d = distAlSegmento(p, previo, actual);
    if (d < min) min = d;
    previo = actual;
  }
  return min;
}

// Que arista queda debajo de `p`, para intercalar un nodo en ella.
//
// `excluir` son los nodos que se estan arrastrando: una arista que sale o llega
// a uno de ellos no es candidata, porque intercalar un nodo en su propia flecha
// no significa nada.
//
// La tolerancia va en unidades de LIENZO, no de pantalla: asi el area sensible
// acompaña al zoom y no se vuelve imposible de acertar al alejarse.
export const TOLERANCIA_INTERCALAR = 26;

export function aristaBajoPunto(edges, nodos, p, excluir = null, tol = TOLERANCIA_INTERCALAR) {
  let mejor = null, mejorD = tol;
  for (const e of Object.values(edges || {})) {
    if (excluir && (excluir.has(String(e.from_task_id)) || excluir.has(String(e.to_task_id))))
      continue;
    const g = edgeGeom(e, nodos);
    if (!g) continue;
    const d = distanciaAArista(g, p);
    if (d < mejorD) { mejorD = d; mejor = String(e.id); }
  }
  return mejor;
}

// Al soltar mientras se crea una arista: se busca el puerto de entrada mas
// cercano y, si no hay ninguno a tiro, vale el cuerpo de cualquier nodo. Exigir
// punteria sobre un circulito de 4,5 px es la queja numero uno de estos
// editores.
export function findDropTarget(nodos, p, fromId, orden) {
  let best = null, bestD = 34;
  for (const n of Object.values(nodos || {})) {
    if (String(n.id) === String(fromId)) continue;
    if (!puertosDe(n).in) continue;
    const ip = portPoint(n, "in");
    const d = Math.hypot(ip.x - p.x, ip.y - p.y);
    if (d < bestD) { best = { nodeId: String(n.id), port: "in" }; bestD = d; }
  }
  if (best) return best;
  const dentro = hitNode(nodos, p, orden);
  if (dentro && String(dentro.id) !== String(fromId) && puertosDe(dentro).in)
    return { nodeId: String(dentro.id), port: "in" };
  return null;
}

// ── Colocacion automatica ────────────────────────────────────────────────────
// El criterio, fijado a proposito y en este orden:
//
//   EJE X   profundidad de dependencias, por camino MAS LARGO. Un paso cae tan
//           a la derecha como su cadena de predecesores mas larga, asi que un
//           atajo nunca adelanta a un paso que en realidad va despues. El tiempo
//           no entra aqui: para eso esta la Cronologia, y mezclarlos daria dos
//           lecturas distintas del mismo diagrama.
//
//   EJE Y   baricentro de las filas de los padres ya colocados —que es lo que
//           reduce los cruces— y a igualdad manda el orden manual de la lista de
//           Pasos (sort_order), no el alfabetico. Asi hay una palanca real:
//           reordenas la lista y el diagrama obedece.
//
//   RAMAS   en un condicional, el "Si" continua en la MISMA fila (el camino
//           esperado se lee en linea recta) y el "No" cae justo debajo. El orden
//           vertical de las dos ramas deja de depender del resto del grafo.
//
//   BUCLES  las aristas de retorno se quitan ANTES de repartir columnas. Con eso
//           el paso del retrabajo cae por si solo en la columna siguiente a su
//           destino y la flecha de vuelta queda corta. Antes se desterraban a la
//           derecha de todo y el retorno cruzaba el lienzo entero.
//
//   SUELTOS un paso sin ninguna conexion no es un inicio: va a un carril propio
//           debajo del flujo, donde se ve y se puede arrastrar para conectarlo.
//
// Es un Sugiyama recortado: sin minimizacion global de cruces, que para unas
// decenas de pasos no se nota y ahorra doscientas lineas.
export function autoLayout(nodos, aristas, opts = {}) {
  const { colGap = 270, rowGap = 104, x0 = 64, y0 = 64 } = opts;
  const ids = Object.keys(nodos || {});
  if (!ids.length) return {};

  // Orden manual de la lista de Pasos, que es el desempate.
  const orden = id => {
    const v = Number(nodos[id]?.sort_order);
    return Number.isFinite(v) ? v : Number.MAX_SAFE_INTEGER;
  };
  const desempate = (a, b) =>
    orden(a) - orden(b)
    || String(nodos[a].title || "").localeCompare(String(nodos[b].title || ""));

  // Las aristas de retorno no cuentan para colocar: si contaran, el paso del
  // retrabajo tiraria de su destino hacia la derecha y el diagrama se estiraria
  // sin motivo.
  const retorno = new Set(detectCycles(nodos, aristas));
  const arcos = Object.values(aristas || {})
    .filter(e => !retorno.has(String(e.id)))
    .filter(e => nodos[String(e.from_task_id)] && nodos[String(e.to_task_id)]);

  // Sueltos: ni entran ni salen. Se apartan antes de repartir columnas.
  const grado = {};
  ids.forEach(id => { grado[id] = 0; });
  for (const e of Object.values(aristas || {})) {
    const f = String(e.from_task_id), t = String(e.to_task_id);
    if (grado[f] !== undefined) grado[f]++;
    if (grado[t] !== undefined) grado[t]++;
  }
  const sueltos = ids.filter(id => grado[id] === 0).sort(desempate);
  const enFlujo = ids.filter(id => grado[id] > 0);

  const pos = {};
  if (!enFlujo.length) {
    // Solo hay pasos sueltos: se colocan en una rejilla normal y ya.
    sueltos.forEach((id, i) => {
      pos[id] = { x: snap(x0 + i * colGap), y: snap(y0) };
    });
    return pos;
  }

  // ── Columnas: camino mas largo sobre el grafo sin retornos ────────────────
  const salidas = {}, entradas = {}, gradoEntrada = {};
  enFlujo.forEach(id => { salidas[id] = []; entradas[id] = []; gradoEntrada[id] = 0; });
  for (const e of arcos) {
    const f = String(e.from_task_id), t = String(e.to_task_id);
    if (!(f in salidas) || !(t in gradoEntrada)) continue;
    salidas[f].push(t);
    entradas[t].push(e);
    gradoEntrada[t]++;
  }

  const capa = {};
  let cola = enFlujo.filter(id => gradoEntrada[id] === 0).sort(desempate);
  cola.forEach(id => { capa[id] = 0; });
  while (cola.length) {
    const siguiente = [];
    for (const id of cola) {
      for (const t of salidas[id]) {
        capa[t] = Math.max(capa[t] ?? 0, (capa[id] ?? 0) + 1);
        if (--gradoEntrada[t] === 0) siguiente.push(t);
      }
    }
    cola = siguiente;
  }
  // Sin los retornos el grafo es aciclico, asi que Kahn los visita todos. La
  // red de seguridad es para un grafo que llegue con algo raro, no para el caso
  // normal: nadie debe desaparecer del lienzo.
  const maxCapa = Math.max(0, ...Object.values(capa));
  enFlujo.forEach(id => { if (capa[id] === undefined) capa[id] = maxCapa + 1; });

  const columnas = {};
  enFlujo.forEach(id => { (columnas[capa[id]] = columnas[capa[id]] || []).push(id); });
  const niveles = Object.keys(columnas).map(Number).sort((a, b) => a - b);

  // ── Filas: numeros REALES, no indices ─────────────────────────────────────
  // Guardar la fila como un numero real es lo que permite que el "Si" de un
  // condicional caiga en la MISMA fila que el rombo y se lea en linea recta.
  // Con indices 0,1,2… por columna eso no se puede expresar.
  const fila = {};
  for (const l of niveles) {
    const deseada = {};
    let libre = 0;

    for (const id of columnas[l]) {
      const padres = entradas[id] || [];

      // Regla de las ramas: la manda un padre que reparte (un condicional o un
      // switch), y gana sobre el baricentro. La salida numero i va a la fila del
      // padre mas i, asi que las ramas caen en el mismo orden en que estan
      // listadas en el nodo y no en el que decida el resto del grafo. Es la
      // misma regla para los dos: el "Si" de un condicional es su salida 0.
      //
      // En un condicional eso ademas deja la primera rama en linea recta,
      // porque su puerto de salida esta en el centro del rombo. En un switch no:
      // sus puertos se reparten en abanico por el lado derecho, asi que lo que
      // se garantiza es el ORDEN, no la rectitud. Alinear cada rama con su
      // puerto exigiria una rejilla de 22 px en vez de una de carriles.
      //
      // Si al nodo llegan VARIAS ramas del mismo padre (los caminos vuelven a
      // juntarse), no hay una rama que respetar y se cae al baricentro.
      const reparten = padres.filter(e => {
        const p = nodos[String(e.from_task_id)];
        return p?.kind === "condicional" || p?.kind === "switch";
      });
      const ramas = new Set(reparten.map(e => `${e.from_task_id}:${e.from_handle}`));
      if (reparten.length && ramas.size === 1) {
        const e = reparten[0];
        const padre = nodos[String(e.from_task_id)];
        const orden = puertosDe(padre).salidas.indexOf(e.from_handle);
        const filaPadre = fila[String(e.from_task_id)] ?? 0;
        deseada[id] = filaPadre + Math.max(0, orden);
        continue;
      }

      const colocados = padres
        .map(e => fila[String(e.from_task_id)])
        .filter(v => v !== undefined);
      deseada[id] = colocados.length
        ? colocados.reduce((s, v) => s + v, 0) / colocados.length
        : libre++;          // sin padres colocados: se apila por orden de lista
    }

    // Se ordena por la fila deseada y, a igualdad, por el orden de la lista.
    const ordenadas = [...columnas[l]].sort((a, b) =>
      (deseada[a] - deseada[b]) || desempate(a, b));

    // Y se resuelven los choques empujando hacia abajo, sin reindexar: quien
    // pueda quedarse en su fila deseada se queda, y ahi esta la linea recta.
    let ultima = -Infinity;
    for (const id of ordenadas) {
      const f = Math.max(deseada[id], ultima + 1);
      fila[id] = f;
      ultima = f;
    }
    columnas[l] = ordenadas;
  }

  // ── A pixeles ─────────────────────────────────────────────────────────────
  // No se centra cada columna por separado: centrarlas rompia justo las lineas
  // rectas que la regla de las ramas acaba de conseguir.
  const filaMin = Math.min(...Object.values(fila));
  for (const l of niveles) {
    for (const id of columnas[l]) {
      const g = geoDe(nodos[id]);
      pos[id] = {
        x: snap(x0 + l * colGap),
        // El CARRIL se ajusta a la rejilla; la compensacion por el alto del nodo
        // se aplica despues y sin ajustar. Es al reves de como estaba, y
        // importa: ajustando el resultado final, un rombo (88 de alto) quedaba
        // 4 px descentrado respecto a una tarea (64) de su misma fila, y la
        // linea del "Si" salia torcida justo donde tiene que ir recta. Lo que
        // debe coincidir son los CENTROS, que es por donde entran y salen las
        // aristas, no las esquinas.
        y: snap(y0 + (fila[id] - filaMin) * rowGap) + (NODE_GEO.tarea.h - g.h) / 2,
      };
    }
  }

  // ── Carril de los sueltos, debajo de todo ─────────────────────────────────
  if (sueltos.length) {
    const abajo = Math.max(...Object.values(pos).map(p => p.y)) + rowGap * 2;
    const paso = Math.round(colGap * 0.8);
    sueltos.forEach((id, i) => {
      const g = geoDe(nodos[id]);
      pos[id] = {
        x: snap(x0 + i * paso),
        y: snap(abajo) + (NODE_GEO.tarea.h - g.h) / 2,
      };
    });
  }

  return pos;
}

// Aristas de retorno, por DFS de tres colores: una arista que apunta a un nodo
// todavia "en curso" cierra un bucle. Devuelve ids de ARISTA, no de nodo, que es
// lo que el lienzo necesita para pintarlas distinto.
//
// Escrito recursivo por legibilidad. Con mas de unos miles de nodos desbordaria
// la pila, cosa que no pasa en un proyecto; si algun dia hiciera falta, se
// convierte a pila explicita.
export function detectCycles(nodos, aristas) {
  const color = {}, salidas = {}, retorno = [];
  Object.keys(nodos || {}).forEach(id => { color[id] = 0; salidas[id] = []; });
  for (const e of Object.values(aristas || {})) {
    const f = String(e.from_task_id);
    if (salidas[f]) salidas[f].push(e);
  }
  const visitar = id => {
    color[id] = 1;
    for (const e of salidas[id]) {
      const t = String(e.to_task_id);
      if (color[t] === 1) retorno.push(String(e.id));
      else if (color[t] === 0) visitar(t);
    }
    color[id] = 2;
  };
  Object.keys(nodos || {}).forEach(id => { if (color[id] === 0) visitar(id); });
  return retorno;
}

// ── Ruta critica ─────────────────────────────────────────────────────────────
// Camino mas largo del grafo dirigido aciclico, por programacion dinamica sobre
// el orden topologico. Si hay bucles no se puede calcular y se dice, en lugar de
// devolver un numero inventado.
export function rutaCritica(nodos, aristas, objetivoDia = 8) {
  const ciclos = detectCycles(nodos, aristas);
  if (ciclos.length) return { ids: [], dias: 0, ciclo: true, aristasCiclo: ciclos };

  const ids = Object.keys(nodos || {});
  if (!ids.length) return { ids: [], dias: 0, ciclo: false, aristasCiclo: [] };

  // Duracion en dias: la estimacion si la hay, si no el hueco entre fechas, y
  // en ultimo caso un dia. Los nodos de control no duran.
  const dur = {};
  for (const id of ids) {
    const n = nodos[id];
    if ((n.kind || "tarea") !== "tarea") { dur[id] = 0; continue; }
    const est = Number(n.estimacion_min) || 0;
    if (est) { dur[id] = est / 60 / objetivoDia; continue; }
    if (n.start_date && n.due_date) {
      const p = s => { const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
                       return Date.UTC(y, m - 1, d); };
      dur[id] = Math.max(1, Math.round((p(n.due_date) - p(n.start_date)) / 86400000) + 1);
      continue;
    }
    dur[id] = 1;
  }

  const salidas = {}, gradoEntrada = {}, entradas = {};
  ids.forEach(id => { salidas[id] = []; gradoEntrada[id] = 0; entradas[id] = []; });
  for (const e of Object.values(aristas || {})) {
    const f = String(e.from_task_id), t = String(e.to_task_id);
    if (!(f in salidas) || !(t in gradoEntrada)) continue;
    salidas[f].push(t); entradas[t].push(f); gradoEntrada[t]++;
  }

  const orden = [];
  let cola = ids.filter(id => gradoEntrada[id] === 0);
  while (cola.length) {
    const siguiente = [];
    for (const id of cola) {
      orden.push(id);
      for (const t of salidas[id]) if (--gradoEntrada[t] === 0) siguiente.push(t);
    }
    cola = siguiente;
  }

  const fin = {}, previo = {};
  for (const id of orden) {
    let mejor = 0, de = null;
    for (const p of entradas[id]) {
      if ((fin[p] ?? 0) > mejor) { mejor = fin[p]; de = p; }
    }
    fin[id] = mejor + dur[id];
    previo[id] = de;
  }

  // El final de la ruta es el mayor `fin`, pero hay que deshacer los empates a
  // conciencia: un nodo de control ('fin') dura cero, asi que empata con el paso
  // que lo precede, y quedarse con el paso cortaria la ruta antes del final del
  // flujo. Con igual duracion gana el que no tiene salidas y, si aun empatan, el
  // que va mas tarde en el orden topologico.
  const rango = {};
  orden.forEach((id, i) => { rango[id] = i; });
  let ultimo = null;
  for (const id of ids) {
    if (ultimo === null) { ultimo = id; continue; }
    const d = (fin[id] ?? 0) - (fin[ultimo] ?? 0);
    if (d > 1e-9) { ultimo = id; continue; }
    if (d < -1e-9) continue;
    const sinSalida    = salidas[id].length === 0;
    const otroSinSalida = salidas[ultimo].length === 0;
    if (sinSalida !== otroSinSalida) { if (sinSalida) ultimo = id; continue; }
    if ((rango[id] ?? -1) > (rango[ultimo] ?? -1)) ultimo = id;
  }

  const camino = [];
  for (let cur = ultimo; cur != null; cur = previo[cur]) camino.unshift(cur);
  return {
    ids: camino,
    dias: Math.round((fin[ultimo] || 0) * 10) / 10,
    ciclo: false,
    aristasCiclo: [],
  };
}
